import { Button, cn } from "@win-reality/react-ui";
import { motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";

type Item = {
  label: string;
  active: boolean;
  onClick: () => void;
};

type HorizontalMenuProps = {
  items: Item[];
  wrapperClassName?: string;
};

function HorizontalMenu({ items, wrapperClassName }: HorizontalMenuProps) {
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const [indicatorStyle, setIndicatorStyle] = useState<{
    left: number;
    width: number;
    opacity: number;
  }>({ left: 0, width: 0, opacity: 0 });

  const updateIndicatorStyle = useCallback(() => {
    const activeItemIndex = items.findIndex((item) => item.active);

    if (activeItemIndex !== -1) {
      const activeItemNode = itemRefs.current[activeItemIndex];

      if (activeItemNode) {
        setIndicatorStyle({
          left: activeItemNode.offsetLeft,
          width: activeItemNode.offsetWidth,
          opacity: 1,
        });
      }
    } else {
      setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
    }
  }, [items]);

  useEffect(() => {
    updateIndicatorStyle();

    const observer = new ResizeObserver(() => {
      updateIndicatorStyle();
    });

    const wrapperNode = wrapperRef.current;
    if (wrapperNode) {
      observer.observe(wrapperNode);
    }

    return () => {
      if (wrapperNode) {
        observer.unobserve(wrapperNode);
      }
    };
  }, [updateIndicatorStyle]);

  return (
    <div className="relative w-full">
      <div className="absolute left-0 right-0 top-[1.6rem] h-[1px] bg-gray-200" />
      <div
        ref={wrapperRef}
        className={cn(
          "relative flex flex-row justify-center gap-4",
          wrapperClassName,
        )}
      >
        <motion.div
          transition={{
            type: "spring",
            stiffness: 350,
            damping: 30,
            duration: 0.2,
          }}
          animate={indicatorStyle}
          className="absolute top-[1.6rem] z-10 h-[0.125rem] bg-blue"
        />

        {items.map((item, index) => (
          <div
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            key={item.label}
          >
            <Button
              onClick={item.onClick}
              variant="ghost"
              className={cn(
                "relative !h-[1.6rem] px-[0.75rem] font-regular text-sm hover:bg-[hsla(218,100%,52%,0.05)]",
                item.active && "text-blue",
              )}
            >
              {item.label}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export { HorizontalMenu };
